import React, { useEffect, useState } from 'react'

// Import Swiper styles
import 'swiper/css'
import 'swiper/css/navigation'

// components
import ActivityTable from 'components/Table/ActivityTable'
import ActivityAccordion from 'components/Accordion/ActivityAccordion'

// custom functions
import { useViewport } from 'utils'

// api
import ActivityAPI from 'apis/activity'
import LoadingComponent from 'pages/explore/LoadingComponent'
import NoDataSection from 'components/pages/Dashboard/NoDataSection'
import { ActivityAPIData } from 'types/nft'

import styles from './index.module.scss'

const ActivitySection = props => {
  const { collectionId, filterKey, activityCollections, setActivityCollections } = props
  const [loading, setLoading] = useState(false)
  const viewport = useViewport()
  const [allList, setAllList] = useState<ActivityAPIData[]>([])
  const [activityList, setActivityList] = useState<ActivityAPIData[]>([])
  const [filteredList, setFilteredList] = useState<ActivityAPIData[]>([])

  const [rowCount, setRowCount] = useState(10)
  const [isFetching, setIsFetching] = useState(false)
  const [isVisibleFetching, setIsVisibleFetching] = useState(false)
  const [filterList, setFilterList] = useState([
    { id: 0, label: 'All Filter', search: '' },
    { id: 1, label: 'Auction Started', search: 'AuctionStarted' },
    { id: 2, label: 'Auction Cancelled', search: 'AuctionCancelled' },
    { id: 3, label: 'Listing Added', search: 'ListingAdded' },
    { id: 4, label: 'Listing Cancelled', search: 'ListingCancelled' },
    { id: 5, label: 'Offer Made', search: 'OfferMade' },
    { id: 6, label: 'Offer Cancelled', search: 'OfferCancelled' },
    { id: 7, label: 'Purchased', search: 'Purchased' },
    { id: 8, label: 'Auction Won', search: 'AuctionWon' },
    { id: 9, label: 'Bid Placed', search: 'BidPlaced' },
  ])

  useEffect(() => {
    if (activityCollections.length > 0) {
      setLoading(true)
      setFilteredList(activityCollections.slice(0, rowCount))
      setAllList(activityCollections)
      setActivityList(activityCollections)
      if (activityCollections.length > rowCount) setIsVisibleFetching(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (activityCollections.length > 0) return

    ActivityAPI.getCollectionActivity(collectionId).then(
      response => {
        let tempItem: any[] = []
        response.reverse().map((item, index) => {
          tempItem.push(item)
        })
        setAllList(tempItem)
        setActivityList(tempItem.slice(0, rowCount))
        setActivityCollections(tempItem)
        setFilteredList(tempItem.slice(0, rowCount))

        if (tempItem.length > rowCount) setIsVisibleFetching(true)
        setLoading(true)
      },
      error => {}
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionId])

  useEffect(() => {
    if (isFetching) {
      setIsFetching(false)
      if (filteredList.length + rowCount > activityList.length) {
        setFilteredList([...filteredList, ...activityList.slice(filteredList.length, activityList.length)])
        setIsVisibleFetching(false)
      } else {
        setFilteredList([...filteredList, ...activityList.slice(filteredList.length, filteredList.length + rowCount)])
        setIsVisibleFetching(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching])

  const getFilterValues = () => {
    switch (filterKey) {
      case 'List':
        return [
          'AuctionStarted',
          'ListingAdded',
          'ListingUpdated',
          'ListingSold',
          'MasterEditionListingCreated',
          'MasterEditionListingUpdated',
        ]
      case 'Delist':
        return ['AuctionCancelled', 'ListingCancelled', 'MasterEditionListingCancelled']
      case 'Offer':
        return ['OfferMade', 'OfferCancelled']
      case 'Buy':
        return ['QuickBuy', 'Purchased', 'AuctionWon', 'MasterEditionPrintCreated']
      case 'Bid':
        return ['BidPlaced']
      default:
        return filterKey
    }
  }

  useEffect(() => {
    if (filterKey === null) {
      if (activityCollections.length > 0) {
        let filtered = []
        setAllList(activityCollections)
        setActivityList(activityCollections)
        setFilteredList(activityCollections.slice(0, rowCount))
      }
      return
    }

    setFilteredList([])

    if (filterKey === '') {
      setActivityList(allList)
      setFilteredList(allList.slice(0, rowCount))
      return
    }

    let filtered = []
    allList.forEach(element => {
      let filterValues = getFilterValues()
      if (filterValues.includes(element.action_type)) filtered.push(element)
    })
    setActivityList(filtered)
    setFilteredList(filtered.slice(0, rowCount))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey])

  return (
    <section className="w-full mt-[16px] lg:mt-[0px] min-h-[250px]">
      <div className="w-full z-10 mt-[10px] lg:mt-[15px]">
        {loading === false ? (
          Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="w-full mt-[25px] px-[15px]">
              <div className="w-full hidden lg:flex items-center justify-between">
                <div className="w-full flex gap-[15px] items-center">
                  <div className="min-w-[77px] max-w-[77px] h-[41px] rounded-[10px] overflow-hidden">
                    <div className="skeletonLoader"></div>
                  </div>
                  <div className="min-w-[41px] max-w-[41px] h-[41px] rounded-[10px] overflow-hidden">
                    <div className="skeletonLoader"></div>
                  </div>
                  <div className="w-1/2 h-[21px] rounded-[11px] overflow-hidden">
                    <div className="skeletonLoader"></div>
                  </div>
                </div>
                <div className="w-[191px] h-[21px] rounded-[10px] overflow-hidden">
                  <div className="skeletonLoader"></div>
                </div>
              </div>

              <div className="w-full flex lg:hidden items-center justify-between">
                <div className="w-full flex gap-[15px] items-center">
                  <div className="min-w-[41px] max-w-[41px] h-[41px] rounded-[10px] overflow-hidden">
                    <div className="skeletonLoader"></div>
                  </div>
                  <div className="w-full">
                    <div className="w-full h-[16px] rounded-[8px] overflow-hidden">
                      <div className="skeletonLoader"></div>
                    </div>
                    <div className="w-1/2 h-[16px] rounded-[8px] overflow-hidden mt-[8px]">
                      <div className="skeletonLoader"></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.border}></div>
            </div>
          ))
        ) : filteredList.length > 0 ? (
          <div>
            {viewport === 'lg' || viewport === 'xl' || viewport === '2xl' ? (
              <ActivityTable data={filteredList} type={'dashboard'} />
            ) : (
              <ActivityAccordion data={filteredList} />
            )}
            {isVisibleFetching && (
              <div className="w-full mt-[100px] mb-[40px]">
                <LoadingComponent isFetching={isFetching} setIsFetching={setIsFetching} text="Loading activity" />
              </div>
            )}
          </div>
        ) : (
          <div className="mt-[70px] lg:mt-[170px]">
            <NoDataSection title="" description="No activity detected" />
          </div>
        )}
      </div>
    </section>
  )
}

export default ActivitySection
